import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import { MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { VoiceModeContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useAvatarPanel } from './AvatarPanel.context';

type VoiceMode = 'text' | 'voice' | 'video';

type Props = {
  /** When false, the Voice option is hidden (feature-flag off). */
  showVoiceOption: boolean;
};

/**
 * VoiceModeDropdown — replaces the Voice Mode switch in the Digital Twin chat.
 *
 * Options:
 *  - Text  (default) — voice mode off, avatar collapsed
 *  - Voice           — voice mode on,  avatar collapsed
 *  - Video           — voice mode off, avatar expanded to full width
 *
 * When showVoiceOption is false (chatVoiceMode feature flag off), only
 * Text and Video are shown.
 *
 * Derives the active mode from VoiceModeContext + AvatarPanelContext so
 * that external state changes (e.g. the ExpandButton in HeyGenPanel) keep
 * the dropdown in sync automatically.
 */
export const VoiceModeDropdown = ({ showVoiceOption }: Props) => {
  const voicemode = useContext(VoiceModeContext);
  const { onClickVoiceMode } = useContext(InputControlsContext);
  const { expanded, setExpanded } = useAvatarPanel();

  /**
   * Derive the currently active mode from the two state sources.
   * Guard voice behind showVoiceOption so that if the feature flag is toggled
   * off while voice is still enabled in context, the Select value stays within
   * the rendered MenuItem set (avoids MUI out-of-range warning).
   */
  const currentMode = useMemo((): VoiceMode => {
    if (expanded) return 'video';
    if (voicemode.enabled && showVoiceOption) return 'voice';
    return 'text';
  }, [expanded, voicemode.enabled, showVoiceOption]);

  const handleChange = useCallback((e: SelectChangeEvent<VoiceMode>) => {
    const next = e.target.value as VoiceMode;

    switch (next) {
      case 'text':
        // Disable voice if it was on, collapse avatar.
        if (voicemode.enabled) onClickVoiceMode();
        setExpanded(false);
        break;
      case 'voice':
        // Enable voice if it wasn't on, collapse avatar.
        if (!voicemode.enabled) onClickVoiceMode();
        setExpanded(false);
        break;
      case 'video':
        // Disable voice (keep it clean per spec), expand avatar.
        if (voicemode.enabled) onClickVoiceMode();
        setExpanded(true);
        break;
    }
  }, [voicemode.enabled, onClickVoiceMode, setExpanded]);

  return (
    <StyledSelect
      value={currentMode}
      onChange={handleChange}
      variant="outlined"
      size="small"
      renderValue={value => <SelectedValue mode={value} />}
    >
      <StyledMenuItem value="video">
        <MenuIcon><VideocamOutlinedIcon fontSize="small" /></MenuIcon>
        Video
      </StyledMenuItem>

      <StyledMenuItem value="text">
        <MenuIcon><ChatBubbleOutlineIcon fontSize="small" /></MenuIcon>
        Text
      </StyledMenuItem>

      {showVoiceOption && (
        <StyledMenuItem value="voice">
          <MenuIcon><VolumeUpOutlinedIcon fontSize="small" /></MenuIcon>
          Voice
        </StyledMenuItem>
      )}
    </StyledSelect>
  );
};

/** The compact trigger shown when the dropdown is closed. */
const SelectedValue = ({ mode }: { mode: VoiceMode }) => {
  const Icon = modeIcon(mode);
  const label = mode.charAt(0).toUpperCase() + mode.slice(1);
  return (
    <SelectedContent>
      <Icon fontSize="small" />
      <span>{label}</span>
    </SelectedContent>
  );
};

function modeIcon(mode: VoiceMode) {
  if (mode === 'video') return VideocamOutlinedIcon;
  if (mode === 'voice') return VolumeUpOutlinedIcon;
  return ChatBubbleOutlineIcon;
}


const StyledSelect = styled(Select<VoiceMode>)({
  height: 28,
  fontSize: 13,
  color: '#555',
  background: 'transparent',

  '& fieldset': {
    borderColor: '#ddd !important',
    borderRadius: 6,
  },

  '&:hover fieldset': {
    borderColor: '#aaa !important',
  },

  '& .MuiSelect-select': {
    padding: '2px 28px 2px 8px !important',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
});

const SelectedContent = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 1;
`;

const StyledMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 6px 14px;
`;

const MenuIcon = styled.span`
  display: flex;
  align-items: center;
  color: #555;
`;
